import { z } from 'zod';

/**
 * Plannen (Stap 14, ADR-0078): herhalingen, ploegtijden en stille uren op één plek.
 *
 * Tijden zijn `HH:MM` in de tijdzone van het bedrijf, zoals bij de checklists (ADR-0064) en de
 * ploegoverdracht (ADR-0074): een `time` zonder zone suggereert een nauwkeurigheid die er niet is.
 * Dagen van de week zijn 1 (maandag) tot 7 (zondag).
 */
const clockSchema = z
  .string()
  .regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/, 'Gebruik UU:MM, bijvoorbeeld 06:00.');
const weekdaysSchema = z.array(z.number().int().min(1).max(7)).max(7);

export const recurrenceSchema = z.enum([
  'once',
  'daily',
  'weekdays',
  'weekly',
  'monthly',
  'yearly',
]);
export type Recurrence = z.infer<typeof recurrenceSchema>;

/** Wat er gepland wordt: een bericht (sjabloon = concept) of een checklist (sjabloon in `tasks`). */
export const scheduleKindSchema = z.enum(['post', 'checklist']);
export type ScheduleKind = z.infer<typeof scheduleKindSchema>;

/** `at_time`: op het uur. `shift_start`: bij het begin van de eerstvolgende dienst van een groep. */
export const deliveryModeSchema = z.enum(['at_time', 'shift_start']);
export type DeliveryMode = z.infer<typeof deliveryModeSchema>;

export const scheduleStatusSchema = z.enum(['active', 'paused', 'cancelled', 'completed']);
export type ScheduleStatus = z.infer<typeof scheduleStatusSchema>;

/** Wanneer iets gebeurt, zonder het wat: ook het voorbeeld van de eerstvolgende vijf data. */
export const scheduleTimingSchema = z.object({
  recurrence: recurrenceSchema,
  /** Bij `weekly`: de dagen. Leeg bij de rest. */
  daysOfWeek: weekdaysSchema.default([]),
  /** Bij `monthly` en `yearly`: de dag. Bestaat hij niet in een maand (31 april), dan de laatste. */
  dayOfMonth: z.number().int().min(1).max(31).nullable().default(null),
  /** Bij `yearly`: de maand (1-12). */
  monthOfYear: z.number().int().min(1).max(12).nullable().default(null),
  timeOfDay: clockSchema,
  /** De eerste dag; bij `once` de dag zelf. */
  startsOn: z.string().date(),
  endsOn: z.string().date().nullable().default(null),
  deliveryMode: deliveryModeSchema.default('at_time'),
  /** Bij `shift_start`: de groep wiens eerstvolgende dienst het moment bepaalt. */
  shiftGroupId: z.guid().nullable().default(null),
});
export type ScheduleTiming = z.infer<typeof scheduleTimingSchema>;

export const createScheduleRequestSchema = scheduleTimingSchema.extend({
  kind: scheduleKindSchema.default('post'),
  targetId: z.guid(),
});
export type CreateScheduleRequest = z.infer<typeof createScheduleRequestSchema>;

export const updateScheduleRequestSchema = scheduleTimingSchema.partial();
export type UpdateScheduleRequest = z.infer<typeof updateScheduleRequestSchema>;

export const scheduleSchema = z.object({
  id: z.guid(),
  kind: scheduleKindSchema,
  targetId: z.guid(),
  /** De titel van het bericht of de naam van de checklist, om de lijst leesbaar te maken. */
  targetTitle: z.string().nullable(),
  recurrence: recurrenceSchema,
  daysOfWeek: z.array(z.number().int()),
  dayOfMonth: z.number().int().nullable(),
  monthOfYear: z.number().int().nullable(),
  timeOfDay: z.string(),
  startsOn: z.string(),
  endsOn: z.string().nullable(),
  deliveryMode: deliveryModeSchema,
  shiftGroupId: z.guid().nullable(),
  shiftGroupName: z.string().nullable(),
  status: scheduleStatusSchema,
  /** Het eerstvolgende voorkomen dat nog moet gebeuren; `null` als er geen meer is. */
  nextDueAt: z.string().datetime().nullable(),
  createdByName: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type Schedule = z.infer<typeof scheduleSchema>;

export const scheduleListSchema = z.object({ items: z.array(scheduleSchema) });
export type ScheduleList = z.infer<typeof scheduleListSchema>;

export const occurrenceStatusSchema = z.enum([
  'pending',
  'processing',
  'published',
  'skipped',
  'failed',
  'cancelled',
]);
export type OccurrenceStatus = z.infer<typeof occurrenceStatusSchema>;

export const occurrenceSchema = z.object({
  id: z.guid(),
  scheduleId: z.guid(),
  dueAt: z.string().datetime(),
  status: occurrenceStatusSchema,
  /** Het bericht of de checklist die eruit ontstond. */
  resultId: z.guid().nullable(),
  error: z.string().nullable(),
  processedAt: z.string().datetime().nullable(),
});
export type Occurrence = z.infer<typeof occurrenceSchema>;

export const occurrenceListSchema = z.object({
  items: z.array(occurrenceSchema),
  /** Of "Nu uitvoeren" mag: alleen in de demo-omgeving (vlag `scheduling.run_now`). */
  runNowAllowed: z.boolean(),
});
export type OccurrenceList = z.infer<typeof occurrenceListSchema>;

export const schedulePreviewSchema = z.object({ dates: z.array(z.string().datetime()) });
export type SchedulePreview = z.infer<typeof schedulePreviewSchema>;

// --- Ploegtijden -----------------------------------------------------------------------

/**
 * Een dienst van een groep: "Late ploeg, 14:00-22:00, maandag tot vrijdag".
 *
 * `groupId` is de groep waar de dienst bij hoort (bij de ploegoverdracht: de lijn); `crewGroupId`
 * de ploeg die dan werkt. Een einde vóór het begin is een dienst over middernacht.
 */
export const shiftPatternSchema = z.object({
  id: z.guid(),
  groupId: z.guid(),
  groupName: z.string().nullable(),
  name: z.string(),
  startTime: z.string(),
  endTime: z.string(),
  daysOfWeek: z.array(z.number().int()),
  crewGroupId: z.guid().nullable(),
  crewGroupName: z.string().nullable(),
  active: z.boolean(),
});
export type ShiftPattern = z.infer<typeof shiftPatternSchema>;

export const shiftPatternListSchema = z.object({ items: z.array(shiftPatternSchema) });
export type ShiftPatternList = z.infer<typeof shiftPatternListSchema>;

export const createShiftPatternRequestSchema = z
  .object({
    groupId: z.guid(),
    name: z.string().trim().min(1).max(80),
    startTime: clockSchema,
    endTime: clockSchema,
    daysOfWeek: weekdaysSchema.min(1).default([1, 2, 3, 4, 5, 6, 7]),
    crewGroupId: z.guid().nullable().default(null),
  })
  .refine((value) => value.startTime !== value.endTime, {
    path: ['endTime'],
    message: 'Een dienst kan niet even lang duren als een dag.',
  });
export type CreateShiftPatternRequest = z.infer<typeof createShiftPatternRequestSchema>;

export const updateShiftPatternRequestSchema = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  startTime: clockSchema.optional(),
  endTime: clockSchema.optional(),
  daysOfWeek: weekdaysSchema.min(1).optional(),
  crewGroupId: z.guid().nullable().optional(),
  active: z.boolean().optional(),
});
export type UpdateShiftPatternRequest = z.infer<typeof updateShiftPatternRequestSchema>;

// --- Stille uren ---------------------------------------------------------------------------

/**
 * Stille uren voor push: voor het hele bedrijf of voor een groep. De inbox vult zich meteen; de
 * trilling op de telefoon wacht tot het einde. Een noodmelding negeert ze altijd (Stap 35).
 */
export const quietHoursSchema = z.object({
  scope: z.enum(['tenant', 'group']),
  groupId: z.guid().nullable(),
  groupName: z.string().nullable(),
  startTime: z.string(),
  endTime: z.string(),
});
export type QuietHours = z.infer<typeof quietHoursSchema>;

export const quietHoursListSchema = z.object({ items: z.array(quietHoursSchema) });
export type QuietHoursList = z.infer<typeof quietHoursListSchema>;

/** Eén regel zetten (of met `enabled: false` weghalen). */
export const putQuietHoursRequestSchema = z
  .object({
    scope: z.enum(['tenant', 'group']),
    groupId: z.guid().nullable().default(null),
    startTime: clockSchema.default('22:00'),
    endTime: clockSchema.default('06:00'),
    enabled: z.boolean().default(true),
  })
  .refine((value) => (value.scope === 'group') === (value.groupId !== null), {
    path: ['groupId'],
    message: 'Stille uren voor een groep horen bij een groep.',
  });
export type PutQuietHoursRequest = z.infer<typeof putQuietHoursRequestSchema>;
